import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Award, Users, BookOpen, Star, Zap, Globe, Clock, Sparkles, Lock, Target, Heart, GraduationCap, Briefcase, CheckCircle, FileText, Rocket, Search, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import SmartImage from '../components/SmartImage';
import CourseStateBadge from '../components/CourseStateBadge';
import VPN360Ad from '../components/VPN360Ad';

// Icon map for CMS feature blocks
const ICON_MAP = {
  BookOpen, Award, Users, Shield, Star, Zap, Globe, ArrowRight,
  Lock, Target, Heart, GraduationCap, Briefcase, CheckCircle,
  FileText, Rocket, Search, MapPin, Sparkles,
};
const getIcon = (name) => ICON_MAP[name] || Shield;

// ── static defaults (used when CMS data not available) ─────────────────────────
const DEFAULT_HERO = {
  title: 'IAASP Academy',
  subtitle: 'International Alliance of Advanced Security Programs',
  description: 'Es un centro de investigación en seguridad estratégica que promueve nuevas tendencias de pensamiento en la protección de personas.',
  cta_text: 'Explorar Cursos',
  cta_link: '/courses',
  cta_secondary_text: 'Verificar Certificado',
  cta_secondary_link: '/verificar-certificado',
};

const DEFAULT_FEATURES = {
  title: '¿Por qué elegir IAASP Academy?',
  items: [
    { icon: 'Lock', title: 'Formación especializada en seguridad avanzada', description: 'Programas enfocados en áreas clave como seguridad electrónica, seguridad cibernética, criminología, criminalística e investigación criminal.' },
    { icon: 'Award', title: 'Certificaciones con respaldo internacional', description: 'IAASP Academy comunica certificaciones reconocidas globalmente, ideales para fortalecer el perfil profesional.' },
    { icon: 'Target', title: 'Enfoque profesional y actualizado', description: 'Sus programas están orientados a preparar personas para los retos reales del sector seguridad.' },
    { icon: 'Globe', title: 'Presencia y visión internacional', description: 'Forma parte de una alianza internacional, lo que aporta mayor proyección académica y profesional.' },
    { icon: 'Heart', title: 'Compromiso con Latinoamérica', description: 'IAASP Región LATAM impulsa programas educativos para jóvenes y comunidades en situación vulnerable.' },
    { icon: 'GraduationCap', title: 'Programa E.F.A.N. — Education for All Nations', description: 'Una iniciativa social que promueve el acceso a la educación y apoya a nuevas generaciones.' },
    { icon: 'Briefcase', title: 'Áreas de estudio con alta demanda laboral', description: 'Seguridad cibernética, investigación criminal y seguridad electrónica son sectores con creciente necesidad de profesionales capacitados.' },
    { icon: 'CheckCircle', title: 'Validación y consulta de certificados en línea', description: 'El sitio muestra registros de certificaciones, lo que aporta trazabilidad y confianza.' },
    { icon: 'FileText', title: 'Contenido académico y de asesoría', description: 'La academia publica artículos y documentos relacionados con seguridad electrónica y cibernética.' },
    { icon: 'Rocket', title: 'Ideal para crecer profesionalmente', description: 'Es una opción para quienes buscan diferenciarse, actualizar conocimientos y obtener respaldo académico en seguridad.' },
  ]
};

const DEFAULT_CTA = {
  title: 'Comenzá Tu Formación en Seguridad Hoy',
  subtitle: 'Únete a profesionales de todo el mundo que confían en IAASP para su desarrollo profesional.',
  button_text: 'Crear Cuenta Gratuita',
  button_link: '/register',
};

// ── block renderers ─────────────────────────────────────────────────────────────
const HeroBlock = ({ data = DEFAULT_HERO }) => (
  <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#0b1220] via-[#1a2744] to-[#0b1220]">
    <div className="absolute inset-0 opacity-20">
      <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(197, 164, 101, 0.2) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(197, 164, 101, 0.1) 0%, transparent 50%)' }} />
    </div>
    <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
      <div className="flex justify-center mb-8">
        <img src="/iaasp_logo.png" alt="IAASP" className="w-32 h-32 md:w-40 md:h-40 object-contain drop-shadow-2xl" />
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-4" data-testid="hero-title">{data.title}</h1>
      {data.subtitle && <p className="text-lg sm:text-xl text-[#c5a465] font-medium mb-4">{data.subtitle}</p>}
      {data.description && <p className="text-base sm:text-lg text-white/80 mb-8 max-w-2xl mx-auto">{data.description}</p>}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        {data.cta_text && (
          <Link to={data.cta_link || '/courses'} data-testid="explore-courses-btn">
            <Button size="lg" className="bg-[#c5a465] hover:bg-[#b8964f] text-[#0b1220] font-semibold">
              {data.cta_text}<ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        )}
        {data.cta_secondary_text && (
          <Link to={data.cta_secondary_link || '/register'} data-testid="register-cta-btn">
            <Button size="lg" variant="outline" className="bg-transparent hover:bg-white/10 text-white border-[#c5a465]/50 hover:border-[#c5a465]">
              {data.cta_secondary_text}
            </Button>
          </Link>
        )}
      </div>
    </div>
  </section>
);

const FeaturesBlock = ({ data = DEFAULT_FEATURES }) => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      {data.title && (
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary mb-4">{data.title}</h2>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
        {(data.items || []).map((item, idx) => {
          const Icon = getIcon(item.icon);
          return (
            <Card key={idx} className="p-6 hover:border-accent/50 transition-all hover:shadow-md group" data-testid={`feature-card-${idx}`}>
              <div className="w-11 h-11 rounded-lg bg-accent/10 flex items-center justify-center mb-3 group-hover:bg-accent/20 transition-colors">
                <Icon className="w-5 h-5 text-accent" />
              </div>
              <h3 className="text-base font-heading font-bold text-primary mb-2 leading-tight">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
            </Card>
          );
        })}
      </div>
    </div>
  </section>
);

const StatsBlock = ({ data }) => (
  <section className="py-16 bg-secondary">
    <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
      {data.title && <h2 className="text-2xl font-bold mb-10">{data.title}</h2>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {(data.items || []).map((item, idx) => (
          <div key={idx}>
            <p className="text-3xl font-black text-primary mb-1">{item.value}</p>
            <p className="text-sm text-muted-foreground">{item.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const RichTextBlock = ({ data }) => (
  <section className="py-16">
    <div className="max-w-3xl mx-auto px-6">
      {data.title && <h2 className="text-2xl font-bold mb-6 text-center">{data.title}</h2>}
      {data.content && (
        <div className="lesson-content" dangerouslySetInnerHTML={{ __html: data.content }} />
      )}
    </div>
  </section>
);

const CTABlock = ({ data = DEFAULT_CTA }) => (
  <section className="py-24 bg-gradient-to-br from-[#0b1220] via-[#1a2744] to-[#0b1220]">
    <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
      <img src="/iaasp_logo.png" alt="IAASP" className="w-20 h-20 mx-auto mb-6 opacity-80" />
      <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">{data.title}</h2>
      {data.subtitle && <p className="text-base text-white/80 mb-8">{data.subtitle}</p>}
      {data.button_text && (
        <Link to={data.button_link || '/register'} data-testid="final-cta-btn">
          <Button size="lg" className="bg-[#c5a465] hover:bg-[#b8964f] text-[#0b1220] font-semibold">{data.button_text}</Button>
        </Link>
      )}
    </div>
  </section>
);

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const LatestCoursesBlock = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/courses/?status=published')
      .then(res => setCourses((res.data || []).slice(0, 6)))
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section className="py-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-muted-foreground">Cargando cursos...</p>
        </div>
      </section>
    );
  }

  if (courses.length === 0) return null;

  return (
    <section className="py-20 bg-secondary" data-testid="latest-courses-block">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-3">
              <Sparkles className="w-3.5 h-3.5" /> NOVEDADES
            </div>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary mb-2">Cursos Recientes</h2>
            <p className="text-base text-muted-foreground">Los cursos más nuevos disponibles en IAASP Academy.</p>
          </div>
          <Link to="/courses">
            <Button variant="outline" data-testid="see-all-courses-btn">
              Ver todos los cursos <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <Link
              key={course.id}
              to={`/courses/${course.id}`}
              data-testid={`home-course-card-${course.id}`}
              className="group bg-white rounded-2xl border overflow-hidden hover:shadow-xl hover:border-accent/40 transition-all"
            >
              {course.image_url ? (
                <div className="relative w-full aspect-video overflow-hidden bg-gray-100">
                  <SmartImage
                    src={course.image_url}
                    alt={course.title}
                    fallbackLabel={course.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ) : (
                <div className="w-full aspect-video bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                  <BookOpen className="w-12 h-12 text-primary/30" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  {course.is_free === false && course.price ? (
                    <Badge className="bg-accent/15 text-accent hover:bg-accent/20">USD ${Number(course.price).toFixed(2)}</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Gratis</Badge>
                  )}
                  {course.delivery_mode === 'zoom' && <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Zoom</Badge>}
                  <CourseStateBadge course={course} />
                  {course.hours && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />{course.hours}h
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-heading font-bold text-primary mb-2 line-clamp-2 group-hover:text-accent transition-colors">
                  {course.title}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{course.description}</p>
                {course.instructor_name && (
                  <p className="text-xs text-muted-foreground/70">Instructor: {course.instructor_name}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutIaaspBlock = () => (
  <section className="py-20 bg-white" data-testid="about-iaasp-block">
    <div className="max-w-5xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-3">
            <Shield className="w-3.5 h-3.5" /> ¿QUIÉNES SOMOS?
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary leading-tight">
            IAASP <span className="text-accent">Latinoamérica</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-3">
            Centro de investigación en seguridad estratégica.
          </p>
        </div>
        <div className="md:col-span-8 space-y-5 text-base leading-relaxed text-foreground/90">
          <p>
            <strong>IAASP Latinoamérica</strong> es un centro de investigación en seguridad estratégica
            que promueve nuevas tendencias de pensamiento en la protección de personas de alto nivel
            ejecutivo, integrando <strong>ciberseguridad</strong>, análisis de <strong>riesgos emergentes</strong> y
            estudio del <strong>comportamiento de potenciales perpetradores</strong>.
          </p>
          <p>
            Sus programas se desarrollan bajo el marco de educación informal conforme al{' '}
            <em>artículo 2.6.6.8 del Decreto 1075 de 2015</em> en Colombia.
          </p>
          <div className="flex gap-3 pt-2 flex-wrap">
            <Link to="/about">
              <Button variant="outline" size="sm">Conocer más</Button>
            </Link>
            <Link to="/courses">
              <Button size="sm" className="bg-accent hover:bg-accent/90 text-[#0b1220] font-semibold">
                Ver programas
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const LatestArticlesBlock = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get('/articles/?limit=3')
      .then(r => setItems((r.data || []).slice(0, 3)))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || items.length === 0) return null;

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <section className="py-20 bg-white" data-testid="latest-articles-block">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-3">
              <BookOpen className="w-3.5 h-3.5" /> ÚLTIMAS PUBLICACIONES
            </div>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary mb-2">Artículos & Noticias</h2>
            <p className="text-base text-muted-foreground">Novedades, anuncios y guías profesionales de IAASP Academy.</p>
          </div>
          <Link to="/articulos">
            <Button variant="outline" data-testid="see-all-articles-btn">
              Ver todos los artículos <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((a) => (
            <Link key={a.id} to={`/articulos/${a.slug}`} data-testid={`home-article-card-${a.id}`}
              className="group bg-secondary rounded-2xl overflow-hidden hover:shadow-lg transition-all">
              {a.featured_image ? (
                <div className="aspect-video overflow-hidden">
                  <SmartImage
                    src={a.featured_image}
                    alt={a.title}
                    fallbackLabel={a.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-gradient-to-br from-primary/10 to-accent/10" />
              )}
              <div className="p-5">
                <p className="text-xs text-muted-foreground mb-2">{fmtDate(a.created_at)}</p>
                <h3 className="text-lg font-heading font-bold text-primary mb-2 line-clamp-2 group-hover:text-accent transition-colors">
                  {a.title}
                </h3>
                {a.excerpt && <p className="text-sm text-muted-foreground line-clamp-2">{a.excerpt}</p>}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const BLOCK_RENDERERS = {
  hero: HeroBlock,
  features: FeaturesBlock,
  stats: StatsBlock,
  rich_text: RichTextBlock,
  cta: CTABlock,
  about_iaasp: AboutIaaspBlock,
  latest_courses: LatestCoursesBlock,
  latest_articles: LatestArticlesBlock,
};

// ── main Home ───────────────────────────────────────────────────────────────────
const Home = () => {
  const [blocks, setBlocks] = useState(null); // null = loading, [] = no CMS

  useEffect(() => {
    api.get('/pages/home')
      .then(res => {
        const content = res.data?.content || {};
        setBlocks(content.blocks && content.blocks.length > 0 ? content.blocks : []);
      })
      .catch(() => setBlocks([])); // fall back to static on error
  }, []);

  // Loading: render static defaults while CMS data loads
  if (blocks === null) {
    return (
      <div className="flex flex-col" data-testid="home-page">
        <HeroBlock data={DEFAULT_HERO} />
        <AboutIaaspBlock />
        <FeaturesBlock data={DEFAULT_FEATURES} />
        <LatestCoursesBlock />
        <LatestArticlesBlock />
        <CTABlock data={DEFAULT_CTA} />
      </div>
    );
  }

  // CMS mode: render blocks if available, else static
  if (blocks.length > 0) {
    const sorted = [...blocks].sort((a, b) => (a.order || 0) - (b.order || 0));
    return (
      <div className="flex flex-col" data-testid="home-page">
        {sorted.map(block => {
          const Renderer = BLOCK_RENDERERS[block.type];
          return Renderer ? <Renderer key={block.id} data={block.data} /> : null;
        })}
        <VPN360Ad variant="large" />
      </div>
    );
  }

  // Static fallback
  return (
    <div className="flex flex-col" data-testid="home-page">
      <HeroBlock data={DEFAULT_HERO} />
      <AboutIaaspBlock />
      <FeaturesBlock data={DEFAULT_FEATURES} />
      <LatestCoursesBlock />
      <LatestArticlesBlock />
      <CTABlock data={DEFAULT_CTA} />
      <VPN360Ad variant="large" />
    </div>
  );
};

export default Home;
